import { useCallback, useEffect, useState } from 'react';
import { UserPlus, Pencil, Trash2, Power, X } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchUtilisateurs,
  creerUtilisateur,
  majUtilisateur,
  supprimerUtilisateur,
} from '../lib/utilisateurs';
import { MODULES, ROLES, modulesParDefaut } from '../lib/permissions';
import { PageTitle, StatBlock } from '../components/shared/badges';
import { fmt } from '../components/shared/badges';

const inputCls =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

const vide = { email: '', name: '', password: '', role: ROLES.BASCULE, modules: modulesParDefaut(ROLES.BASCULE), actif: true, peut_deroger_minimum: false };

export default function Utilisateurs() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    fetchUtilisateurs().then(setRows);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const changerRole = (role) => setForm((p) => ({ ...p, role, modules: modulesParDefaut(role) }));

  const basculerModule = (key) =>
    setForm((p) => ({
      ...p,
      modules: p.modules.includes(key) ? p.modules.filter((m) => m !== key) : [...p.modules, key],
    }));

  const enregistrer = async (e) => {
    e.preventDefault();
    const action = form.creation ? creerUtilisateur : majUtilisateur;
    const { error } = await action({ ...form, permissions_personnalisees: true });
    if (error) return toast.error(error);
    toast.success(form.creation ? `Compte de ${form.name} créé` : `Compte de ${form.name} mis à jour`);
    setForm(null);
    load();
  };

  const basculerActif = async (u) => {
    const { error } = await majUtilisateur({ ...u, actif: !u.actif });
    if (error) return toast.error(error);
    toast.success(u.actif ? `${u.name} désactivé` : `${u.name} réactivé`);
    load();
  };

  const supprimer = async (u) => {
    const { error } = await supprimerUtilisateur(u.email);
    if (error) return toast.error(error);
    toast.success(`Compte de ${u.name} supprimé`);
    load();
  };

  return (
    <div data-testid="utilisateurs-page" className="space-y-6">
      <PageTitle
        emoji="🔐"
        titre="Utilisateurs & accès"
        sous="Comptes et accès à la carte — les modules cochés sont vérifiés par le serveur"
        action={
          <button
            type="button"
            data-testid="nouvel-utilisateur"
            onClick={() => setForm({ ...vide, creation: true })}
            className="flex h-11 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 sm:h-10"
          >
            <UserPlus size={16} /> Nouveau compte
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatBlock testid="stat-comptes" titre="Comptes" accent="border-l-slate-900" items={[['Total', fmt(rows.length)]]} />
        <StatBlock testid="stat-actifs" titre="Actifs" accent="border-l-emerald-600" items={[['Comptes', fmt(rows.filter((u) => u.actif).length)]]} />
        <StatBlock testid="stat-admins" titre="Administrateurs" accent="border-l-amber-600" items={[['Comptes', fmt(rows.filter((u) => u.role === ROLES.ADMIN && u.actif).length)]]} />
      </div>

      <section className="border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-utilisateurs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Nom', 'Email', 'Profil', 'Modules', 'Dérogation minimum', 'Statut', 'Actions'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.email} data-testid={`utilisateur-row-${u.email}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm font-semibold text-slate-900">{u.name}</td>
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{u.email}</td>
                  <td className="px-3 py-2 text-sm text-slate-800">{u.role}</td>
                  <td className="px-3 py-2 font-data text-sm text-slate-700" data-testid={`modules-${u.email}`}>
                    {u.modules.length} / {MODULES.length}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      data-testid={`deroger-minimum-${u.email}`}
                      className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${
                        u.role === ROLES.ADMIN || u.peut_deroger_minimum
                          ? 'bg-sky-50 text-sky-800'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {u.role === ROLES.ADMIN ? 'd’office' : u.peut_deroger_minimum ? 'autorisé' : 'refusé'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      data-testid={`statut-${u.email}`}
                      className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${
                        u.actif ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {u.actif ? 'actif' : 'désactivé'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        data-testid={`editer-utilisateur-${u.email}`}
                        title="Modifier"
                        onClick={() => setForm({ ...u, password: '', creation: false })}
                        className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        data-testid={`basculer-actif-${u.email}`}
                        title={u.actif ? 'Désactiver' : 'Réactiver'}
                        onClick={() => basculerActif(u)}
                        className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                      >
                        <Power size={13} />
                      </button>
                      <button
                        type="button"
                        data-testid={`supprimer-utilisateur-${u.email}`}
                        title="Supprimer"
                        onClick={() => supprimer(u)}
                        className="border border-red-300 p-1.5 text-red-600 transition-colors duration-150 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {form && (
        <div data-testid="utilisateur-overlay" className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/60 p-4">
          <form onSubmit={enregistrer} data-testid="utilisateur-form" className="max-h-[92vh] w-full max-w-2xl overflow-y-auto border border-slate-200 bg-white">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">
                  {form.creation ? 'Nouveau compte' : `Modifier — ${form.name}`}
                </h2>
                <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  Profil métier et modules autorisés
                </p>
              </div>
              <button type="button" data-testid="utilisateur-close" onClick={() => setForm(null)} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
              <div>
                <label className={label}>Nom complet</label>
                <input data-testid="u-nom" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Email</label>
                <input
                  data-testid="u-email"
                  type="email" required
                  value={form.email}
                  disabled={!form.creation}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`${inputCls} disabled:bg-slate-100 disabled:text-slate-500`}
                />
              </div>
              <div>
                <label className={label}>Mot de passe {form.creation ? '' : '(vide = inchangé)'}</label>
                <input data-testid="u-password" type="password" minLength={10} required={form.creation} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Profil métier</label>
                <select data-testid="u-role" value={form.role} onChange={(e) => changerRole(e.target.value)} className={inputCls}>
                  {Object.values(ROLES).map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="border-t border-slate-200 px-5 py-4">
              <p className={label}>Modules autorisés ({form.modules.length})</p>
              <p className="mt-2 text-sm text-slate-600">
                {form.role === ROLES.ADMIN
                  ? 'Un administrateur conserve tous les accès. Choisissez un autre profil pour limiter les modules.'
                  : 'Les cases définissent les accès, indépendamment du profil. Les restrictions métier du profil sont conservées. Aucune case cochée = aucun module.'}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {MODULES.map((m) => (
                  <label key={m.key} className="flex cursor-pointer items-center gap-2 border border-slate-200 px-3 py-2 text-sm text-slate-800 transition-colors duration-150 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      data-testid={`u-module-${m.key}`}
                      disabled={form.role === ROLES.ADMIN}
                      checked={form.role === ROLES.ADMIN || form.modules.includes(m.key)}
                      onChange={() => basculerModule(m.key)}
                      className="h-4 w-4 accent-slate-900"
                    />
                    <span aria-hidden="true">{m.emoji}</span> {m.label}
                  </label>
                ))}
              </div>
              <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  data-testid="u-deroger-minimum"
                  checked={form.role === ROLES.ADMIN || form.peut_deroger_minimum === true}
                  disabled={form.role === ROLES.ADMIN}
                  onChange={() => setForm({ ...form, peut_deroger_minimum: !form.peut_deroger_minimum })}
                  className="h-4 w-4 accent-slate-900"
                />
                Peut déroger au minimum de trituration
                <span className="font-data text-[11px] text-slate-500">
                  {form.role === ROLES.ADMIN
                    ? '(d’office pour un Administrateur)'
                    : '— délégation permanente : l’utilisateur décide seul, sans validation à distance'}
                </span>
              </label>
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  data-testid="u-actif"
                  checked={form.actif}
                  onChange={() => setForm({ ...form, actif: !form.actif })}
                  className="h-4 w-4 accent-slate-900"
                />
                Compte actif (connexion autorisée)
              </label>
            </div>

            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button type="submit" data-testid="u-enregistrer" className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700">
                Enregistrer
              </button>
              <button type="button" onClick={() => setForm(null)} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
